import { useQueries } from '@tanstack/react-query';
import type { Server, Schedule, ScheduleRun } from '../api/types.js';
import { useApiClient } from './use-api-client.js';

export interface ServerSchedules {
  server: Server;
  schedules: Schedule[];
  isLoading: boolean;
  error: Error | null;
}

export function useAllSchedules(servers: Server[] | undefined) {
  const api = useApiClient();

  const queries = useQueries({
    queries: (servers ?? []).map((server) => ({
      queryKey: ['schedules', server.id],
      queryFn: () => api.servers.schedules(server.id),
      enabled: !!server.id,
    })),
  });

  const result: ServerSchedules[] = (servers ?? []).map((server, i) => ({
    server,
    schedules: queries[i].data ?? [],
    isLoading: queries[i].isLoading,
    error: queries[i].error,
  }));

  const isLoading = queries.some((q) => q.isLoading);

  return { data: result, isLoading };
}

export interface ServerScheduleRuns {
  server: Server;
  runs: Record<string, ScheduleRun[]>;
  isLoading: boolean;
  error: Error | null;
}

export function useAllScheduleRuns(servers: Server[] | undefined) {
  const api = useApiClient();

  const queries = useQueries({
    queries: (servers ?? []).map((server) => ({
      queryKey: ['scheduleRuns', server.id],
      queryFn: () => api.servers.scheduleRuns(server.id),
      enabled: !!server.id,
    })),
  });

  const result: ServerScheduleRuns[] = (servers ?? []).map((server, i) => ({
    server,
    runs: queries[i].data?.schedules ?? {},
    isLoading: queries[i].isLoading,
    error: queries[i].error,
  }));

  const isLoading = queries.some((q) => q.isLoading);
  return { data: result, isLoading };
}
